import React, { useMemo, useState, useEffect } from 'react'
import styles from './FaqTab.module.scss'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { Pagination } from './Pagination'
import { SortBar } from './SortBar'
import { CategoryFilter } from './CategoryFilter'
import { useFaqList } from '../../hooks'

export const FaqTab = () => {
	const [openId, setOpenId] = useState(null)
	const [isNewestFirst, setIsNewestFirst] = useState(true)
	const [currentPage, setCurrentPage] = useState(1)
	const [selectedCategory, setSelectedCategory] = useState('전체')

	const categoryList = ['결제', '오류', '계약', '문의', '기타']
	const categoryMap = {
		payment: '결제',
		error: '오류',
		contract: '계약',
		inquiry: '문의',
		etc: '기타',
	}
	const reverseCategoryMap = Object.fromEntries(
		Object.entries(categoryMap).map(([k, v]) => [v, k]),
	)

	// 선택된 카테고리에 따른 API 파라미터
	const paramCategory =
		selectedCategory !== '전체' ? reverseCategoryMap[selectedCategory] : undefined

	// React Query 훅 사용
	const { data: faqs, isLoading, error } = useFaqList(paramCategory)

	useEffect(() => {
		setOpenId(null)
	}, [currentPage, isNewestFirst, selectedCategory])

	const itemsPerPage = 10

	const sortedFaqs = useMemo(() => {
		if (!faqs) return []
		const sorted = [...faqs].sort((a, b) => {
			const dateA = new Date(a.date)
			const dateB = new Date(b.date)
			return isNewestFirst ? dateB - dateA : dateA - dateB
		})
		return sorted
	}, [faqs, isNewestFirst])

	const totalPages = Math.ceil(sortedFaqs.length / itemsPerPage)
	const currentItems = sortedFaqs.slice(
		(currentPage - 1) * itemsPerPage,
		currentPage * itemsPerPage,
	)

	const toggle = (id) => {
		setOpenId((prev) => (prev === id ? null : id))
	}

	return (
		<div className={styles.faqPanel}>
			<div className={styles.faqFeature}>
				<CategoryFilter
					categoryList={categoryList}
					selectedCategory={selectedCategory}
					onSelect={setSelectedCategory}
				/>
			</div>
			<div style={{ marginTop: '4px' }}>
				<SortBar
					isNewestFirst={isNewestFirst}
					onToggle={() => setIsNewestFirst((prev) => !prev)}
				/>
			</div>

			<div className={styles.faqWrapper}>
				{isLoading ? (
					<p className={styles.emptyState}>로딩 중...</p>
				) : error ? (
					<p className={styles.emptyState}>FAQ를 불러오는데 실패했습니다.</p>
				) : faqs?.length === 0 ? (
					<p className={styles.emptyState}>자주 묻는 질문이 없습니다.</p>
				) : (
					<>
						<ul className={styles.faqList}>
							{currentItems.map((faq) => (
								<li key={faq.id} className={styles.faqItem}>
									<div
										className={styles.faqHeader}
										onClick={() => toggle(faq.id)}>
										<div className={styles.faqLabel}>
											<span className={styles.faqCategory}>
												{faq.category}
											</span>
											<span className={styles.faqQuestion}>{faq.title}</span>
										</div>
										<div className={styles.faqMeta}>
											<span className={styles.faqDate}>{faq.date}</span>
											{openId === faq.id ? (
												<ChevronUpIcon className={styles.faqIcon} />
											) : (
												<ChevronDownIcon className={styles.faqIcon} />
											)}
										</div>
									</div>
									{openId === faq.id && (
										<div className={styles.faqContent}>
											<div className={styles.divider}></div>
											{faq.content}
										</div>
									)}
								</li>
							))}
						</ul>
						<Pagination
							currentPage={currentPage}
							totalPages={totalPages}
							onPageChange={setCurrentPage}
						/>
					</>
				)}
			</div>
		</div>
	)
}
